/**
 * Phase 15.6N — Collab Picker.
 *
 * Search RGM users by name/email and tag them as collaborators on a
 * track upload/edit. Each collaborator can be assigned a role
 * (Featured / Producer / Collaborator / Beat Credit). Non-RGM
 * collaborators can be added as a manual text credit.
 *
 * Props:
 *   value: array of collaborator items
 *           [{ user_id, artist_name, role, profile_photo?, manual_credit? }, ...]
 *   onChange(nextValue): called with the updated array
 *   currentUserId: prevents tagging the owner of the track
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { optimizeImage } from "@/lib/mediaUrls";

const ROLES = [
  { key: "featured", label: "Featured Artist", icon: "🎤" },
  { key: "producer", label: "Producer", icon: "🎛️" },
  { key: "collaborator", label: "Collaborator", icon: "🤝" },
  { key: "beat_credit", label: "Beat Credit", icon: "🎹" },
];

const CollabPicker = ({ value = [], onChange, currentUserId = null }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [defaultRole, setDefaultRole] = useState("featured");
  const [manualName, setManualName] = useState("");
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setShowResults(false);
      return undefined;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/users/search", { params: { q, limit: 8 } });
        const existingIds = new Set([
          currentUserId,
          ...value.map((v) => v.user_id).filter(Boolean),
        ]);
        setResults((r.data?.users || []).filter((u) => !existingIds.has(u.user_id)));
        setShowResults(true);
      } catch (_e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, value, currentUserId]);

  const addUser = (u) => {
    onChange([
      ...value,
      {
        user_id: u.user_id,
        artist_name: u.artist_name || u.name || "Artist",
        profile_photo: u.profile_photo || u.picture || null,
        role: defaultRole,
      },
    ]);
    setQuery("");
    setResults([]);
    setShowResults(false);
  };

  const addManual = () => {
    const name = manualName.trim();
    if (!name) return;
    onChange([
      ...value,
      {
        user_id: null,
        artist_name: name,
        role: defaultRole,
        manual_credit: true,
      },
    ]);
    setManualName("");
  };

  const remove = (i) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  const updateRole = (i, role) => {
    const next = value.slice();
    next[i] = { ...next[i], role };
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="collab-picker">
      {/* Role selector + search */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-purple-200/80 uppercase tracking-wider">
          Add Collaborator (role applied to next addition)
        </label>
        <div className="flex flex-wrap gap-1.5" data-testid="collab-picker-role-buttons">
          {ROLES.map((r) => (
            <button
              type="button"
              key={r.key}
              onClick={() => setDefaultRole(r.key)}
              className={`px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${
                defaultRole === r.key
                  ? "bg-purple-500 text-white"
                  : "bg-white/5 text-purple-200/70 hover:bg-white/10"
              }`}
              data-testid={`collab-picker-role-${r.key}`}
            >
              {r.icon} {r.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by artist name or email…"
            className="bg-black/30 border-purple-500/30 text-white"
            data-testid="collab-picker-search-input"
            onFocus={() => results.length > 0 && setShowResults(true)}
          />
          {searching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-purple-300/60">…</div>
          )}
          {showResults && results.length > 0 && (
            <div
              className="absolute left-0 right-0 top-full mt-1 z-20 bg-[#1a1a1e] border border-purple-500/30 rounded-lg shadow-xl max-h-64 overflow-y-auto"
              data-testid="collab-picker-results"
            >
              {results.map((u) => (
                <button
                  type="button"
                  key={u.user_id}
                  onClick={() => addUser(u)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-purple-500/10 text-left"
                  data-testid={`collab-picker-result-${u.user_id}`}
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center flex-shrink-0">
                    {u.profile_photo || u.picture ? (
                      <img src={optimizeImage(u.profile_photo || u.picture, 'avatar')} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-xs font-bold">{(u.artist_name || "?").charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 leading-tight">
                    <div className="text-white text-sm truncate">
                      {u.artist_name || u.name || "Artist"}
                    </div>
                    {u.handle && (
                      <div className="text-[11px] text-purple-300/70 truncate font-mono">
                        @{u.handle}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Manual text credit */}
      <details className="bg-black/20 border border-purple-500/15 rounded-lg p-3" data-testid="collab-picker-manual-section">
        <summary className="text-xs text-purple-200/70 cursor-pointer">
          Collaborator not on RGM? Add a manual text credit
        </summary>
        <div className="flex gap-2 mt-2">
          <Input
            type="text"
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            placeholder="e.g. DJ Crazy"
            maxLength={80}
            className="bg-black/30 border-purple-500/30 text-white text-sm"
            data-testid="collab-picker-manual-input"
          />
          <Button
            type="button"
            onClick={addManual}
            disabled={!manualName.trim()}
            className="bg-purple-600 hover:bg-purple-500 text-white"
            data-testid="collab-picker-manual-add"
          >
            Add
          </Button>
        </div>
      </details>

      {/* Selected collaborators */}
      {value.length > 0 && (
        <div className="space-y-1.5" data-testid="collab-picker-selected">
          <label className="text-xs font-semibold text-purple-200/80 uppercase tracking-wider">
            Selected ({value.length})
          </label>
          {value.map((c, i) => {
            const roleMeta = ROLES.find((r) => r.key === c.role) || ROLES[0];
            return (
              <div
                key={`${c.user_id || c.artist_name}-${i}`}
                className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-lg p-2"
                data-testid={`collab-picker-selected-${c.user_id || c.artist_name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="w-7 h-7 rounded-full overflow-hidden bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center flex-shrink-0">
                  {c.profile_photo ? (
                    <img src={optimizeImage(c.profile_photo, 'avatar')} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white text-xs font-bold">{(c.artist_name || "?").charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <span className="text-white text-sm truncate flex-1 min-w-0">
                  {c.artist_name}
                  {c.manual_credit && <span className="ml-1 text-[10px] text-purple-200/60">(off-platform)</span>}
                </span>
                <select
                  value={c.role}
                  onChange={(e) => updateRole(i, e.target.value)}
                  className="bg-black/40 border border-purple-500/30 text-purple-100 text-xs rounded px-1.5 py-1"
                  data-testid={`collab-picker-role-select-${i}`}
                >
                  {ROLES.map((r) => (
                    <option key={r.key} value={r.key}>{roleMeta.icon === r.icon ? r.label : `${r.icon} ${r.label}`}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-rose-300 hover:text-rose-200 text-xs px-2"
                  data-testid={`collab-picker-remove-${i}`}
                  aria-label={`Remove ${c.artist_name}`}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CollabPicker;
